import { Danger, Send2 } from 'iconsax-react';

import { Alert, AlertDescription } from '@src/design-system/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import { Badge } from '@src/design-system/badge';
import { Button } from '@src/design-system/button';
import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@src/design-system/dialog';
import { LivePreview } from '@src/shared/features/reminders/live-preview';
import type { TReminderLog } from '@src/shared/types/reminder';
import { REMINDER_LOG_STATUSES } from '@src/shared/types/reminder';
import { formatTextForDisplay } from '@src/shared/utils/helpers/format-text-for-display';
import { ReminderLogResendButton } from '@src/features/reminders/components/all-reminders/automated-reminders/reminder-log-resend-button';

function recipientDisplayName(log: TReminderLog) {
  const { recipient, lead } = log;
  if (lead) {
    return lead.name;
  }
  return [recipient.first_name, recipient.last_name].filter(Boolean).join(' ');
}

function initials(log: TReminderLog) {
  const { recipient, lead } = log;
  const a = lead ? (lead.name?.[0] ?? '') : (recipient?.first_name?.[0] ?? '');
  const b = lead ? (lead.name?.[1] ?? '') : (recipient?.last_name?.[0] ?? '');
  const s = `${a}${b}`.toUpperCase();
  return s || '?';
}

export function ViewReminderLogDialogContent({ log }: { log: TReminderLog }) {
  const { reminder, recipient, failure_reason, status, lead } = log;

  const programName = reminder.program?.name ?? '—';
  const fullName = recipientDisplayName(log) || '—';
  const showFailureBanner =
    Boolean(failure_reason?.trim()) || status === REMINDER_LOG_STATUSES.failed;

  return (
    <div className='flex h-full min-h-0 w-full flex-col overflow-hidden'>
      <DialogHeader className='shrink-0'>
        <DialogTitle>{reminder.name}</DialogTitle>
      </DialogHeader>

      <DialogBody className='flex h-auto min-h-0 flex-1 flex-col overflow-hidden p-0'>
        <div className='flex min-h-0 flex-1 flex-col gap-0 overflow-hidden md:flex-row'>
          <div className='flex min-h-0 w-full shrink-0 flex-col gap-4 overflow-y-auto border-gray-100 p-6 md:w-1/2 md:max-h-full md:border-r md:pr-6'>
            {showFailureBanner && (
              <Alert
                type='fill'
                variant='destructive'
              >
                <Danger
                  size={24}
                  variant='Bold'
                />
                <AlertDescription>
                  {failure_reason?.trim()
                    ? failure_reason
                    : 'This reminder could not be delivered.'}
                </AlertDescription>
              </Alert>
            )}

            <div className='flex items-center justify-between rounded-xl border border-gray-200 bg-white p-4'>
              <div className='flex items-start gap-3 pr-20'>
                <Avatar className='size-14 shrink-0 border border-gray-100'>
                  <AvatarImage
                    src={recipient?.avatar}
                    alt=''
                  />
                  <AvatarFallback className='text-sm font-medium'>
                    {initials(log)}
                  </AvatarFallback>
                </Avatar>
                <div className='min-w-0 pt-0.5'>
                  <p className='truncate text-base font-semibold text-gray-900'>
                    {fullName}
                  </p>
                  <p className='truncate text-sm text-gray-500'>
                    {programName}
                  </p>
                </div>
              </div>
              <Badge
                variant='danger'
                className='p-2 rounded-xl text-sm capitalize'
              >
                {formatTextForDisplay(status)}
              </Badge>
            </div>

            <div className='rounded-xl border border-gray-200 bg-white p-4 text-sm'>
              <dl className='grid gap-3'>
                <div>
                  <dt className='text-xs font-medium text-gray-500'>
                    Full Name
                  </dt>
                  <dd className='text-gray-900'>{fullName}</dd>
                </div>
                <div>
                  <dt className='text-xs font-medium text-gray-500'>Email</dt>
                  <dd className='break-all text-gray-900'>
                    {lead ? lead.email || '—' : recipient?.email || '—'}
                  </dd>
                </div>
                <div>
                  <dt className='text-xs font-medium text-gray-500'>
                    Phone Number
                  </dt>
                  <dd className='text-gray-900'>
                    {lead
                      ? lead.phone_number || '—'
                      : recipient?.phone_number || '—'}
                  </dd>
                </div>
                <div>
                  <dt className='text-xs font-medium text-gray-500'>
                    Whatsapp Number
                  </dt>
                  <dd className='text-gray-900'>
                    {lead
                      ? lead.whatsapp_number || '—'
                      : recipient?.whatsapp_number || '—'}
                  </dd>
                </div>
              </dl>
            </div>
            <DialogFooter className='grid grid-cols-2 mt-auto p-0 pt-4 w-full shrink-0 flex-row items-center justify-between gap-3 border-t border-gray-100 sm:flex-row'>
              {/* <Button
                variant='primary'
                size='lg'
                className='min-w-[140px]'
                onClick={() => onResend?.()}
              >
                <Send2
                  size={20}
                  variant='TwoTone'
                />
                Resend
              </Button> */}
              <ReminderLogResendButton
                trigger={
                  <Button
                    variant='primary'
                    size='lg'
                    className='min-w-full'
                  >
                    <Send2
                      size={20}
                      variant='TwoTone'
                    />
                    Resend
                  </Button>
                }
                ids={[log.id]}
              />
              <DialogClose asChild>
                <Button
                  variant='primaryOutline'
                  size='lg'
                  className='min-w-[140px]'
                >
                  Cancel
                </Button>
              </DialogClose>
            </DialogFooter>
          </div>

          <div className='flex min-h-[min(280px,45vh)] w-full flex-1 flex-col bg-gray-50 p-6 md:min-h-0 md:w-1/2'>
            <LivePreview
              attachments={reminder.attachments ?? []}
              channel={log.channel || reminder.notification_channel}
              className='min-h-0 flex-1'
              content={reminder.content ?? ''}
              defaultMode='live'
              subject={reminder.subject}
            />
          </div>
        </div>
      </DialogBody>
    </div>
  );
}
